/**
 * StripeMetadataSection Component
 *
 * Accordion section for mapping parameters to Stripe PaymentIntent metadata.
 * Part of the "DEFINE → USE" pattern: uses defined parameters in Stripe destination.
 *
 * Features:
 * - 2-field pattern for each parameter (Source Type → Source Item)
 * - Stripe metadata keys use the internal key as defined in sources
 * - Max keys limit (Stripe limit: 50)
 * - Priority fields configuration
 * - All values are converted to strings (Stripe requirement)
 *
 * @module webplacements/components/editors/destinations
 */

import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tooltip,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ParameterDefinition } from '@modules/webplacements/types/webplacements.types';
import { DestinationParameterRow, type DestinationParameterMapping } from './DestinationParameterRow';

export interface StripeMetadataSectionProps {
  /** All defined parameters from all sources */
  availableParameters: ParameterDefinition[];
  /** Current Stripe metadata mappings */
  mappings: DestinationParameterMapping[];
  /** Called when mappings change */
  onMappingsChange: (mappings: DestinationParameterMapping[]) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
}

const STRIPE_MAX_KEYS = 50;

/**
 * StripeMetadataSection Component
 *
 * Provides an interface for selecting which parameters to include in Stripe metadata.
 */
export const StripeMetadataSection: React.FC<StripeMetadataSectionProps> = ({
  availableParameters,
  mappings,
  onMappingsChange,
  disabled = false,
}) => {
  const theme = useTheme();

  const handleAddMapping = useCallback(() => {
    if (availableParameters.length === 0) return;
    if (mappings.length >= STRIPE_MAX_KEYS) return;

    // Find first parameter not already mapped
    const existingKeys = new Set(mappings.map((m) => m.sourceKey));
    const firstAvailable = availableParameters.find((p) => !existingKeys.has(p.key));
    const param = firstAvailable || availableParameters[0];

    const newMapping: DestinationParameterMapping = {
      sourceType: param.source,
      sourceKey: param.key,
    };

    onMappingsChange([...mappings, newMapping]);
  }, [availableParameters, mappings, onMappingsChange]);

  const handleMappingChange = useCallback(
    (index: number, mapping: DestinationParameterMapping) => {
      const newMappings = [...mappings];
      newMappings[index] = mapping;
      onMappingsChange(newMappings);
    },
    [mappings, onMappingsChange]
  );

  const handleRemoveMapping = useCallback(
    (index: number) => {
      onMappingsChange(mappings.filter((_, i) => i !== index));
    },
    [mappings, onMappingsChange]
  );

  const isAtLimit = mappings.length >= STRIPE_MAX_KEYS;

  return (
    <Box>
      {/* Header with Add Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon fontSize="small" sx={{ color: '#6772e5' }} />
          <Typography variant="body2" color="text.secondary">
            Select parameters to include in Stripe PaymentIntent metadata
          </Typography>
          <Tooltip title="Parameters are attached to Stripe PaymentIntent metadata using their internal key as defined in sources. Visible in Stripe Dashboard and queryable via API. All values are converted to strings.">
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
        </Box>
        {/* Add Button - top right */}
        <Button
          data-testid="wp-stripe-meta-add-btn"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddMapping}
          disabled={disabled || availableParameters.length === 0 || isAtLimit}
        >
          Add Parameter to Stripe
        </Button>
      </Box>

      {/* Keys Counter */}
      {mappings.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Using {mappings.length} of {STRIPE_MAX_KEYS} keys
        </Typography>
      )}

      {/* Mappings List */}
      {mappings.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          {mappings.map((mapping, index) => (
            <DestinationParameterRow
              key={index}
              index={index}
              availableParameters={availableParameters}
              value={mapping}
              onChange={(m) => handleMappingChange(index, m)}
              onRemove={() => handleRemoveMapping(index)}
              disabled={disabled}
            />
          ))}
        </Paper>
      )}

      {/* At Limit Warning */}
      {isAtLimit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="caption">
            Maximum keys ({STRIPE_MAX_KEYS}) reached. Remove a parameter to add more.
          </Typography>
        </Alert>
      )}

      {/* Preview */}
      {mappings.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            bgcolor: theme.palette.action.hover,
            borderStyle: 'dashed',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Stripe metadata preview:
          </Typography>
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              m: 0,
              p: 1,
              bgcolor: theme.palette.background.default,
              borderRadius: 1,
              overflow: 'auto',
              color: theme.palette.text.primary,
            }}
          >
            {JSON.stringify(
              Object.fromEntries(
                mappings
                  .filter((m) => m.sourceKey)
                  .map((m) => [m.sourceKey, 'sample_value'])
              ),
              null,
              2
            )}
          </Box>
        </Paper>
      )}

      {/* Empty State */}
      {mappings.length === 0 && availableParameters.length === 0 && (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Define parameters in the sections above first.
          </Typography>
        </Box>
      )}

      {mappings.length === 0 && availableParameters.length > 0 && (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No parameters added to Stripe metadata yet.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default StripeMetadataSection;
